import { useState, useEffect, useMemo, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw, Trash2, Users as UsersIcon, Clock, CheckCircle2, Circle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import AdminSearchPanel from "@/components/admin/AdminSearchPanel";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

function timeAgo(iso) {
  try {
    const t = new Date(iso);
    const diff = Math.max(0, Date.now() - t.getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return "?"; }
}

const ROLE_COLORS = {
  patient: "bg-blue-100 text-blue-700",
  prescriber: "bg-purple-100 text-purple-700",
  admin: "bg-amber-100 text-amber-700",
  customer_service: "bg-teal-100 text-teal-700",
  it: "bg-rose-100 text-rose-700",
  anonymous: "bg-slate-100 text-slate-600 dark:text-slate-300",
};

const STATE_PILLS = [
  { key: "open", label: "Active", color: "bg-rose-600" },
  { key: "acknowledged", label: "Acknowledged", color: "bg-amber-500" },
  { key: "resolved", label: "Resolved", color: "bg-emerald-600" },
  { key: "all", label: "All", color: "bg-slate-600" },
];

function StateBadge({ state }) {
  if (state === "acknowledged") return <Badge className="bg-amber-100 text-amber-800 gap-1"><Circle className="w-3 h-3 fill-amber-500 text-amber-500" /> Acknowledged</Badge>;
  if (state === "resolved") return <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" /> Resolved</Badge>;
  return <Badge className="bg-rose-100 text-rose-700 gap-1"><Circle className="w-3 h-3 fill-rose-500 text-rose-500" /> Active</Badge>;
}

export default function ErrorReportsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [stateFilter, setStateFilter] = useState("open");
  const [busyLoc, setBusyLoc] = useState(null);
  const [filters, setFilters] = useState({ q: "", days: "" });

  const FILTER_DEFS = useMemo(() => ([
    { key: "days", label: "Time range", type: "days" },
  ]), []);

  const load = useCallback(async (s = stateFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ state: s });
      if (filters.q) params.set("q", filters.q);
      if (filters.days) params.set("days", filters.days);
      const { data } = await API.get(`/admin/errors/summary?${params.toString()}`);
      setData(data);
    } catch { toast.error("Failed to load errors"); }
    finally { setLoading(false); }
  }, [stateFilter, filters.q, filters.days]);
  useEffect(() => {
    // Debounce when the free-text query is the change driver; immediate for
    // pill/state-filter changes.
    const t = setTimeout(() => load(stateFilter), filters.q ? 300 : 0);
    const poll = setInterval(() => load(stateFilter), 30000);
    return () => { clearTimeout(t); clearInterval(poll); };
  }, [load, stateFilter, filters.q]);

  const triage = async (location, action) => {
    setBusyLoc(location);
    try {
      await API.post("/admin/errors/triage", { location, action });
      const msg = { ack: "Acknowledged", resolve: "Resolved", reopen: "Reopened" }[action] || "Updated";
      toast.success(msg);
      load();
    } catch { toast.error("Failed to update"); }
    finally { setBusyLoc(null); }
  };

  const clearAll = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Clear ALL client error logs? This cannot be undone.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    try {
      const { data } = await API.delete("/admin/errors");
      toast.success(`Cleared ${data.deleted} error logs`);
      load();
    } catch { toast.error("Failed to clear"); }
  };

  if (loading && !data) return <p className="text-slate-400 dark:text-slate-500 text-sm py-8 text-center">Loading error reports...</p>;

  const groups = data?.groups || [];
  const total = data?.total || 0;
  const counts = data?.counts_by_state || { open: 0, acknowledged: 0, resolved: 0 };

  return (
    <div className="space-y-6" data-testid="error-reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <AlertTriangle className="w-6 h-6 text-rose-600" /> Error Reports
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-1">Live feed of uncaught frontend errors. Ack & Resolve to coordinate triage.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => load(stateFilter)} variant="ghost" size="sm" data-testid="refresh-errors-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          {total > 0 && <Button onClick={clearAll} variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="clear-errors-btn"><Trash2 className="w-4 h-4 mr-1" /> Clear all</Button>}
        </div>
      </div>

      {/* State filter pills */}
      <div className="flex gap-2 flex-wrap" data-testid="state-filter">
        {STATE_PILLS.map((p) => {
          const count = p.key === "all" ? (counts.open + counts.acknowledged + counts.resolved) : (counts[p.key] || 0);
          const active = stateFilter === p.key;
          return (
            <button key={p.key} onClick={() => setStateFilter(p.key)}
              data-testid={`state-filter-${p.key}`}
              className={`text-sm px-4 py-1.5 rounded-full inline-flex items-center gap-2 transition-colors ${active ? `${p.color} text-white` : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40"}`}>
              {p.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100 text-slate-500 dark:text-slate-400 dark:text-slate-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <AdminSearchPanel
        scope="errors"
        value={filters}
        onChange={(v) => setFilters({ q: "", days: "", ...v })}
        filters={FILTER_DEFS}
        searchPlaceholder="Search by message, URL, user, or stack snippet…"
        total={groups.length}
        resultCount={groups.length}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 uppercase tracking-wide">Total Events</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{total}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 uppercase tracking-wide">Unique Errors</p><p className="text-2xl font-bold text-rose-600">{groups.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 uppercase tracking-wide">Last 24h</p><p className="text-2xl font-bold text-amber-600">{groups.filter((g) => (Date.now() - new Date(g.last_seen).getTime()) < 86400000).length}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Grouped by Message ({groups.length})</CardTitle></CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <p className="text-emerald-600 text-sm py-10 text-center">
              {stateFilter === "open" ? "No active client errors. Your frontend is running clean." :
               stateFilter === "acknowledged" ? "No errors are currently being worked on." :
               stateFilter === "resolved" ? "No errors have been resolved yet." :
               "No client errors recorded."}
            </p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="errors-list">
              {groups.map((g, idx) => {
                const isOpen = expanded === idx;
                const busy = busyLoc === g.location;
                return (
                  <div key={g.location} className={`rounded-xl border transition-colors ${g.state === "resolved" ? "border-emerald-100 bg-emerald-50/30" : g.state === "acknowledged" ? "border-amber-100 bg-amber-50/30" : "border-slate-200 dark:border-slate-800 hover:border-rose-200"}`} data-testid={`error-group-${idx}`}>
                    <div className="w-full p-4 flex items-start gap-3">
                      <button onClick={() => setExpanded(isOpen ? null : idx)} className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5" aria-label="Expand">
                        <AlertTriangle className="w-4 h-4 text-rose-600" />
                      </button>
                      <button onClick={() => setExpanded(isOpen ? null : idx)} className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white truncate max-w-[520px]">{g.message}</span>
                          <Badge className="bg-rose-100 text-rose-700">×{g.count}</Badge>
                          <StateBadge state={g.state} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(g.last_seen)}</span>
                          <span className="text-slate-400 dark:text-slate-500 truncate">{g.location}</span>
                          {g.users.length > 0 && <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{g.users.length} user{g.users.length !== 1 ? "s" : ""}</span>}
                          {g.roles.map((r) => <span key={r} className={`px-2 py-0.5 rounded-full text-xs ${ROLE_COLORS[r] || "bg-slate-100 text-slate-600 dark:text-slate-300"}`}>{r}</span>)}
                        </div>
                        {(g.ack_by_name || g.resolved_by_name) && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-1.5 italic">
                            {g.state === "resolved" && g.resolved_by_name ? `✓ Resolved by ${g.resolved_by_name} · ${timeAgo(g.resolved_at)}` :
                             g.ack_by_name ? `⧗ Acknowledged by ${g.ack_by_name} · ${timeAgo(g.ack_at)}` : null}
                          </p>
                        )}
                      </button>
                      <div className="flex-shrink-0 flex gap-1.5 flex-wrap justify-end">
                        {g.state === "open" && (
                          <>
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => triage(g.location, "ack")} className="rounded-lg h-8 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid={`ack-btn-${idx}`}>
                              <Circle className="w-3.5 h-3.5 mr-1" /> Ack
                            </Button>
                            <Button size="sm" disabled={busy} onClick={() => triage(g.location, "resolve")} className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`resolve-btn-${idx}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                            </Button>
                          </>
                        )}
                        {g.state === "acknowledged" && (
                          <>
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => triage(g.location, "reopen")} className="rounded-lg h-8" data-testid={`reopen-btn-${idx}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                            </Button>
                            <Button size="sm" disabled={busy} onClick={() => triage(g.location, "resolve")} className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`resolve-btn-${idx}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                            </Button>
                          </>
                        )}
                        {g.state === "resolved" && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => triage(g.location, "reopen")} className="rounded-lg h-8" data-testid={`reopen-btn-${idx}`}>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                          </Button>
                        )}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-800/40 rounded-b-xl">
                        <div className="pt-3 space-y-2 text-xs">
                          <div><span className="font-semibold text-slate-600 dark:text-slate-300">First seen:</span> <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">{new Date(g.first_seen).toLocaleString()}</span></div>
                          <div><span className="font-semibold text-slate-600 dark:text-slate-300">Last seen:</span> <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">{new Date(g.last_seen).toLocaleString()}</span></div>
                          <div><span className="font-semibold text-slate-600 dark:text-slate-300">Affected users:</span> <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">{g.users.length ? g.users.join(", ") : "none identified"}</span></div>
                          <div><span className="font-semibold text-slate-600 dark:text-slate-300">URL:</span> <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500 font-mono break-all">{g.location}</span></div>
                          <div className="mt-2">
                            <p className="font-semibold text-slate-600 dark:text-slate-300 mb-1">Raw details:</p>
                            <pre className="bg-slate-900 text-slate-200 rounded-lg p-3 overflow-x-auto text-[11px] font-mono whitespace-pre-wrap break-words">{g.details_sample}</pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
